import React, { useState, useMemo } from 'react';
import { Combobox } from '@headlessui/react';
import { ChevronDown, X } from 'lucide-react';

export interface SearchableOption {
  id: string;
  label: string;
  description?: string;
}

interface MultiSearchableSelectProps {
  options: SearchableOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  maxHeight?: string;
}

export const MultiSearchableSelect: React.FC<MultiSearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search and select...',
  disabled = false,
  isLoading = false,
  emptyMessage = 'No options found',
  maxHeight = 'max-h-60',
}) => {
  const [query, setQuery] = useState('');

  // Filter options based on search query, excluding already selected items
  const filteredOptions = useMemo(() => {
    const filtered = options.filter((option) => {
      // Hide already selected items from dropdown
      if (value.includes(option.id)) return false;

      const searchTerm = query.toLowerCase();
      return (
        option.label.toLowerCase().includes(searchTerm) ||
        option.description?.toLowerCase().includes(searchTerm) ||
        option.id.toLowerCase().includes(searchTerm)
      );
    });

    return filtered;
  }, [query, options, value]);

  // Get selected options for display
  const selectedOptions = useMemo(
    () => options.filter((opt) => value.includes(opt.id)),
    [value, options]
  );

  const handleRemove = (id: string) => {
    onChange(value.filter((v) => v !== id));
  };

  const handleAdd = (id: string) => {
    onChange([...value, id]);
    setQuery('');
  };

  return (
    <div className="space-y-2">
      {/* Selected items display */}
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2 rounded-lg border border-primary-200 bg-primary-50 p-2">
          {selectedOptions.map((option) => (
            <div
              key={option.id}
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-sm text-white"
            >
              <span>{option.label}</span>
              <button
                type="button"
                onClick={() => handleRemove(option.id)}
                className="rounded-full p-0.5 transition-colors hover:bg-primary-600"
                disabled={disabled}
                aria-label={`Remove ${option.label}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Searchable dropdown */}
      <Combobox value={''} onChange={handleAdd} disabled={disabled || isLoading}>
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg border border-border-default bg-surface-base shadow-sm transition-all focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
            <Combobox.Input
              className={`w-full border-none bg-transparent py-2 pl-3 pr-10 text-sm leading-5 text-content-primary placeholder:text-content-muted focus:outline-none ${
                disabled || isLoading ? 'cursor-not-allowed bg-surface-raised' : ''
              }`}
              placeholder={placeholder}
              onChange={(event) => setQuery(event.target.value)}
              value={query}
              readOnly={disabled || isLoading}
            />

            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              {isLoading ? (
                <div className="animate-spin">
                  <ChevronDown size={16} className="text-gray-400" />
                </div>
              ) : (
                <ChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform ${
                    query ? 'rotate-180' : ''
                  }`}
                />
              )}
            </div>
          </div>

          {/* Dropdown options */}
          <Combobox.Options
            className={`absolute z-10 ${maxHeight} w-full overflow-auto rounded-lg border border-border-default bg-surface-base py-1 shadow-lg`}
          >
            {filteredOptions.length === 0 ? (
              <div className="relative cursor-default select-none px-4 py-2 text-content-muted">
                {isLoading ? 'Loading...' : emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <Combobox.Option
                  key={option.id}
                  value={option.id}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-4 ${
                      active ? 'bg-primary-500 text-white' : 'text-content-primary'
                    }`
                  }
                >
                  {({ active }) => (
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="font-normal">{option.label}</div>
                        {option.description && (
                          <p
                            className={`text-xs ${
                              active ? 'text-primary-100' : 'text-content-muted'
                            }`}
                          >
                            {option.description}
                          </p>
                        )}
                      </div>
                      <span
                        className={`text-sm flex-shrink-0 ${
                          active ? 'text-white' : 'text-content-muted'
                        }`}
                      >
                        +
                      </span>
                    </div>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </div>
      </Combobox>

      {/* Help text */}
      {selectedOptions.length === 0 && (
        <p className="text-xs text-content-muted">
          Click to search and select items. Click the + button to add them.
        </p>
      )}
    </div>
  );
};
